import React, { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import {
  Add as AddIcon,
  DeleteOutline,
  ExpandLess,
  ExpandMore,
} from "@mui/icons-material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import PageHeader from "../../components/layout/PageHeader";
import journalAPI, {
  CreateJournalData,
  JournalEntry,
} from "../../services/journalAPI";
import chartOfAccountsAPI from "../../services/chartOfAccountsAPI";
import { branchAPI } from "../../services/branchAPI";
import { salesPointAPI } from "../../services/salesPointAPI";
import { useAuthStore } from "../../stores/authStore";
import { useRBAC } from "../../hooks/useRBAC";
import { indiaFyMeta } from "../../services/financialYearAPI";

const formatCurrency = (value: number) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 2,
  }).format(Number(value) || 0);

type DraftLine = {
  key: string;
  ledgerCode: string;
  debit: string;
  credit: string;
};

const emptyLine = (): DraftLine => ({
  key: `${Date.now()}-${Math.random()}`,
  ledgerCode: "",
  debit: "",
  credit: "",
});

const JournalsPage: React.FC = () => {
  const queryClient = useQueryClient();
  const { user } = useAuthStore();
  const { canCreate } = useRBAC();
  const role = String(user?.role || "").toUpperCase();
  const isAdmin = role === "ADMIN";

  const fy = indiaFyMeta();
  const [startDate, setStartDate] = useState(fy.startDate);
  const [endDate, setEndDate] = useState(() => {
    const today = format(new Date(), "yyyy-MM-dd");
    return today < fy.endDate ? today : fy.endDate;
  });
  const [expanded, setExpanded] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [alert, setAlert] = useState<{
    type: "success" | "error";
    message: string;
  } | null>(null);

  const [entryDate, setEntryDate] = useState(format(new Date(), "yyyy-MM-dd"));
  const [branchId, setBranchId] = useState(
    isAdmin ? user?.branchId || "" : ""
  );
  const [salesPointId, setSalesPointId] = useState(user?.salesPointId || "");
  const [narration, setNarration] = useState("");
  const [lines, setLines] = useState<DraftLine[]>([emptyLine(), emptyLine()]);

  const { data = [], isLoading, error, isFetching } = useQuery({
    queryKey: ["journals", startDate, endDate],
    queryFn: () => journalAPI.list({ startDate, endDate }),
  });

  const { data: groups = [] } = useQuery({
    queryKey: ["chart-of-accounts"],
    queryFn: () => chartOfAccountsAPI.list(),
  });

  const { data: branches = [] } = useQuery({
    queryKey: ["branchesAll"],
    queryFn: () => branchAPI.getAllBranches(),
  });

  const { data: salesPoints = [] } = useQuery({
    queryKey: ["salesPointsAll"],
    queryFn: () => salesPointAPI.getAllSalesPoints(),
  });

  const ledgers = useMemo(
    () =>
      groups.flatMap((g) =>
        (g.ledgers || [])
          .filter((l) => l.isActive !== false)
          .map((l) => ({ code: l.code, name: l.name, group: g.name }))
      ),
    [groups]
  );

  const filteredSalesPoints = useMemo(() => {
    if (!branchId) return salesPoints;
    return salesPoints.filter((s) => s.branchId === branchId);
  }, [salesPoints, branchId]);

  const draftTotals = useMemo(() => {
    let debit = 0;
    let credit = 0;
    for (const l of lines) {
      debit += Number(l.debit) || 0;
      credit += Number(l.credit) || 0;
    }
    return { debit, credit, balanced: Math.abs(debit - credit) < 0.009 };
  }, [lines]);

  const { mutate: createJournal, isPending: saving } = useMutation({
    mutationFn: (payload: CreateJournalData) => journalAPI.create(payload),
    onSuccess: (row) => {
      queryClient.invalidateQueries({ queryKey: ["journals"] });
      setOpen(false);
      setAlert({
        type: "success",
        message: `Journal ${row.entryNumber} posted.`,
      });
    },
    onError: (err: any) => {
      setAlert({
        type: "error",
        message: err.response?.data?.message || "Failed to create journal",
      });
    },
  });

  const toggle = (id: string) => {
    setExpanded((prev) => (prev === id ? null : id));
  };

  const openCreate = () => {
    setEntryDate(format(new Date(), "yyyy-MM-dd"));
    setBranchId(isAdmin ? user?.branchId || "" : "");
    setSalesPointId(user?.salesPointId || "");
    setNarration("");
    setLines([emptyLine(), emptyLine()]);
    setOpen(true);
  };

  const updateLine = (key: string, patch: Partial<DraftLine>) => {
    setLines((prev) =>
      prev.map((l) => (l.key === key ? { ...l, ...patch } : l))
    );
  };

  const handleCreate = () => {
    setAlert(null);
    if (!salesPointId) {
      setAlert({ type: "error", message: "Sales point is required." });
      return;
    }
    const payloadLines = lines
      .map((l) => ({
        ledgerCode: l.ledgerCode,
        debit: Number(l.debit) || 0,
        credit: Number(l.credit) || 0,
      }))
      .filter((l) => l.ledgerCode && (l.debit > 0 || l.credit > 0));

    if (payloadLines.length < 2) {
      setAlert({
        type: "error",
        message: "Enter at least two lines with ledger and amount.",
      });
      return;
    }
    if (!draftTotals.balanced) {
      setAlert({
        type: "error",
        message: "Debits must equal credits.",
      });
      return;
    }

    createJournal({
      entryDate,
      branchId: branchId || undefined,
      salesPointId,
      narration: narration.trim() || undefined,
      lines: payloadLines,
    });
  };

  return (
    <Box>
      <PageHeader
        title="Journals"
        subtitle="Double-entry postings (auto + manual)"
        breadcrumbs={[
          { label: "Dashboard", to: "/dashboard" },
          { label: "Reports", to: "/reports" },
          { label: "Journals" },
        ]}
        action={
          canCreate("reports") ? (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={openCreate}
            >
              New journal
            </Button>
          ) : undefined
        }
      />

      {alert && (
        <Alert
          severity={alert.type}
          sx={{ mb: 2 }}
          onClose={() => setAlert(null)}
        >
          {alert.message}
        </Alert>
      )}

      <Box
        sx={{
          p: 2,
          mb: 2,
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 1,
        }}
      >
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
          <TextField
            label="From"
            type="date"
            size="small"
            InputLabelProps={{ shrink: true }}
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
          <TextField
            label="To"
            type="date"
            size="small"
            InputLabelProps={{ shrink: true }}
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
          />
        </Stack>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {(error as Error).message || "Failed to load journals"}
        </Alert>
      )}

      {isLoading || isFetching ? (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      ) : data.length === 0 ? (
        <Typography color="text.secondary">
          No journal entries in this period. Complete a sale/recharge, create a
          voucher, or post a manual journal.
        </Typography>
      ) : (
        <Stack spacing={1}>
          {data.map((entry: JournalEntry) => {
            const isOpen = expanded === entry.id;
            return (
              <Box
                key={entry.id}
                sx={{
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 1,
                }}
              >
                <Stack
                  direction="row"
                  alignItems="center"
                  sx={{ px: 2, py: 1.5, cursor: "pointer" }}
                  onClick={() => toggle(entry.id)}
                >
                  <Box flex={1}>
                    <Typography fontWeight={700}>
                      {entry.entryNumber}{" "}
                      <Typography
                        component="span"
                        color="text.secondary"
                        fontWeight={400}
                      >
                        · {entry.entryDate}
                      </Typography>
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {entry.narration || "—"} · {entry.sourceType}
                    </Typography>
                  </Box>
                  <Chip
                    size="small"
                    label={`Dr ${formatCurrency(entry.totalDebit)}`}
                    sx={{ mr: 1 }}
                  />
                  <Chip
                    size="small"
                    color="success"
                    label={`Cr ${formatCurrency(entry.totalCredit)}`}
                    sx={{ mr: 1 }}
                  />
                  <IconButton size="small">
                    {isOpen ? <ExpandLess /> : <ExpandMore />}
                  </IconButton>
                </Stack>
                <Collapse in={isOpen}>
                  <Table size="small" sx={{ px: 1, pb: 1 }}>
                    <TableHead>
                      <TableRow>
                        <TableCell>Ledger</TableCell>
                        <TableCell align="right">Debit</TableCell>
                        <TableCell align="right">Credit</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(entry.lines || []).map((l, idx) => (
                        <TableRow key={l.id || `${entry.id}-${idx}`}>
                          <TableCell>
                            <Typography fontFamily="monospace" fontSize={12}>
                              {l.ledgerCode}
                            </Typography>
                            {l.ledgerName}
                          </TableCell>
                          <TableCell align="right">
                            {l.debit ? formatCurrency(l.debit) : "—"}
                          </TableCell>
                          <TableCell align="right">
                            {l.credit ? formatCurrency(l.credit) : "—"}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Collapse>
              </Box>
            );
          })}
        </Stack>
      )}

      <Dialog
        open={open}
        onClose={() => !saving && setOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>New journal entry</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField
                label="Date"
                type="date"
                size="small"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={entryDate}
                onChange={(e) => setEntryDate(e.target.value)}
              />
              <FormControl size="small" fullWidth>
                <InputLabel>Branch</InputLabel>
                <Select
                  label="Branch"
                  value={branchId}
                  onChange={(e) => {
                    setBranchId(e.target.value);
                    setSalesPointId("");
                  }}
                >
                  {branches.map((b) => (
                    <MenuItem key={b.id} value={b.id}>
                      {b.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" fullWidth>
                <InputLabel>Sales point</InputLabel>
                <Select
                  label="Sales point"
                  value={salesPointId}
                  onChange={(e) => setSalesPointId(e.target.value)}
                >
                  {filteredSalesPoints.map((s) => (
                    <MenuItem key={s.id} value={s.id}>
                      {s.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>

            <TextField
              label="Narration"
              size="small"
              fullWidth
              value={narration}
              onChange={(e) => setNarration(e.target.value)}
            />

            <Stack spacing={1}>
              {lines.map((line) => (
                <Stack
                  key={line.key}
                  direction={{ xs: "column", sm: "row" }}
                  spacing={1}
                  alignItems={{ sm: "center" }}
                >
                  <FormControl size="small" sx={{ flex: 2, minWidth: 180 }}>
                    <InputLabel>Ledger</InputLabel>
                    <Select
                      label="Ledger"
                      value={line.ledgerCode}
                      onChange={(e) =>
                        updateLine(line.key, { ledgerCode: e.target.value })
                      }
                    >
                      {ledgers.map((l) => (
                        <MenuItem key={l.code} value={l.code}>
                          {l.code} — {l.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <TextField
                    label="Debit"
                    size="small"
                    type="number"
                    sx={{ flex: 1 }}
                    value={line.debit}
                    onChange={(e) =>
                      updateLine(line.key, {
                        debit: e.target.value,
                        credit: e.target.value ? "" : line.credit,
                      })
                    }
                  />
                  <TextField
                    label="Credit"
                    size="small"
                    type="number"
                    sx={{ flex: 1 }}
                    value={line.credit}
                    onChange={(e) =>
                      updateLine(line.key, {
                        credit: e.target.value,
                        debit: e.target.value ? "" : line.debit,
                      })
                    }
                  />
                  <IconButton
                    size="small"
                    disabled={lines.length <= 2}
                    onClick={() =>
                      setLines((prev) => prev.filter((l) => l.key !== line.key))
                    }
                  >
                    <DeleteOutline fontSize="small" />
                  </IconButton>
                </Stack>
              ))}
              <Button
                size="small"
                startIcon={<AddIcon />}
                onClick={() => setLines((prev) => [...prev, emptyLine()])}
                sx={{ alignSelf: "flex-start" }}
              >
                Add line
              </Button>
            </Stack>

            <Typography
              variant="body2"
              color={draftTotals.balanced ? "success.main" : "error.main"}
            >
              Dr {formatCurrency(draftTotals.debit)} · Cr{" "}
              {formatCurrency(draftTotals.credit)}
              {draftTotals.balanced ? " (balanced)" : " (not balanced)"}
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleCreate}
            disabled={saving || !draftTotals.balanced}
          >
            {saving ? "Posting…" : "Post journal"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default JournalsPage;
